import { useCallback } from "react";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { ONE_MB, TWO } from "@/constants/constants";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { useDrive } from "@/context/DriveContext";
import { BatchFileItem } from "@/interfaces/project.interface";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

export const useFileUpload = (): {
  uploadSingleFile: (
    file: File,
    onProgress?: (progress: number) => void,
    onStatusChange?: (status: "uploading" | "success" | "error") => void,
  ) => Promise<void>;
  validateFile: (file: File) => boolean;
  createBatchFileItem: (file: File) => BatchFileItem;
} => {
  const { folderId, activeFolderTarget, type, ID: id } = useDrive();
  const [alterProject] = useAlterProjectMutation();
  const [alterLeadsDrive] = useAlterLeadsDriveMutation();

  const mutationHandler = useCallback(():
    | typeof alterProject
    | typeof alterLeadsDrive => {
    if (type === driveTypes.PROJECT || type === driveTypes.TASK_AND_CLAIMS) {
      return alterProject;
    } else {
      return alterLeadsDrive;
    }
  }, [type, alterProject, alterLeadsDrive]);

  const uploadSingleFile = useCallback(
    async (
      file: File,
      onProgress?: (progress: number) => void,
      onStatusChange?: (status: "uploading" | "success" | "error") => void,
    ): Promise<void> => {
      if (file?.size > DRIVE_CONSTANTS.MAX_FILE_SIZE) {
        customToast.error(DRIVE_CONSTANTS.MESSAGES.FILE_SIZE_EXCEEDED);

        return;
      }

      onStatusChange?.("uploading");
      onProgress?.(0);

      const formData = new FormData();
      // Set the file with the correct MIME type
      formData.append(
        "files",
        new Blob([file], { type: "application/octet-stream" }),
        file.name,
      );

      const structure = {
        directories: [],
        files: [
          {
            name: file.name,
            size: file.size,
            path: [],
          },
        ],
      };

      const targetFolderId = activeFolderTarget || folderId;
      if (targetFolderId) {
        formData.append("parentFolderId", targetFolderId);
        (structure.files[0].path as string[]) = [targetFolderId];
      }

      formData.append("structure", JSON.stringify(structure));
      formData.append(
        "driveType",
        ((): string => {
          if (type === driveTypes.PROJECT) {
            return driveTypes.PROJECT;
          } else if (type === driveTypes.TASK_AND_CLAIMS) {
            return driveTypes.TASK_AND_CLAIMS;
          } else {
            return driveTypes.PROJECT_REGISTER;
          }
        })(),
      );

      try {
        onProgress?.(50);

        const mutation = mutationHandler();
        const res = await mutation({
          ...(type === driveTypes.PROJECT_REGISTER
            ? { leadsId: id }
            : { projectId: id }),
          data: formData,
          method: REQUEST_METHODS.POST,
          params: "drive/batch-upload-structure",
        });

        validateResponse({
          response: res,
          onSuccess: () => {
            onProgress?.(100);
            onStatusChange?.("success");
            customToast.success(DRIVE_CONSTANTS.MESSAGES.DOCUMENT_CREATED);
          },
          onError: () => {
            onStatusChange?.("error");
            customToast.error(
              DRIVE_CONSTANTS.MESSAGES.DOCUMENT_CREATION_FAILED,
            );
          },
        });
      } catch (error) {
        onStatusChange?.("error");
        customToast.error(DRIVE_CONSTANTS.MESSAGES.DOCUMENT_CREATION_FAILED);
        throw error;
      }
    },
    [activeFolderTarget, folderId, type, mutationHandler, id],
  );

  const validateFile = useCallback((file: File): boolean => {
    if (file.size > DRIVE_CONSTANTS.MAX_FILE_SIZE) {
      customToast.error(
        `File ${file.name} exceeds size limit (${(
          DRIVE_CONSTANTS.MAX_FILE_SIZE / ONE_MB
        ).toFixed(TWO)}MB)`,
      );

      return false;
    }

    return true;
  }, []);

  const createBatchFileItem = useCallback(
    (file: File): BatchFileItem => ({
      file,
      path: activeFolderTarget ? [activeFolderTarget] : [],
      status: "pending",
      progress: 0,
    }),
    [activeFolderTarget],
  );

  return {
    uploadSingleFile,
    validateFile,
    createBatchFileItem,
  };
};
